import React,{useState} from 'react';
import {Camera,FileText,FileDown,FileSearch,ShieldCheck} from 'lucide-react';
import {
  validateDanfeFiles,normalizeDanfeDraft,resolveDanfeCatalogItem,
  danfeDraftSafety,serializeDanfeDraftCSV,parseDanfeDecimal
} from './danfeReview.js';

const statusTexts={candidate:'Possível correspondência (conferir)',unmatched:'Localizar produto',
  ambiguous:'Código duplicado',inactive:'Produto inativo',unit_mismatch:'Unidade divergente',
  fractional:'Quantidade fracionária não permitida'};
const exact=v=>String(v??'').trim().toUpperCase();
const fileToBase64=async file=>{
  const bytes=new Uint8Array(await file.arrayBuffer());
  let raw='';
  for(let i=0;i<bytes.length;i+=0x8000){
    raw+=String.fromCharCode(...bytes.subarray(i,i+0x8000));
  }
  return btoa(raw);
};

export default function DanfeReview({organization,products=[],catalogReady=false,canInventory=false,
  scannedKey='',recipientCnpj='',onAnalyze}){
  const [files,setFiles]=useState([]),[consent,setConsent]=useState(false);
  const [busy,setBusy]=useState(false),[error,setError]=useState('');
  const [draft,setDraft]=useState(null),[manualLookup,setManualLookup]=useState({});
  const [fieldEdits,setFieldEdits]=useState({});
  const [origin,setOrigin]=useState('');
  const issues=danfeDraftSafety(draft,{recipientCnpj,scannedKey});
  const setSelected=selection=>{
    const next=Array.from(selection||[]);
    try{validateDanfeFiles(next);setFiles(next);setError('');setDraft(null);setManualLookup({});setFieldEdits({});setConsent(false)}
    catch(e){setFiles([]);setDraft(null);setError(e.message)}
  };
  async function analyze(){
    if(!onAnalyze||!canInventory||!catalogReady||busy||!consent)return;
    setBusy(true);setError('');setDraft(null);setManualLookup({});setFieldEdits({});
    try{
      validateDanfeFiles(files);
      const docs=await Promise.all(files.map(async file=>({
        mime_type:file.type,base64:await fileToBase64(file)
      })));
      const result=await onAnalyze(docs);
      if(!result?.ok||!result?.review_required||result?.stock_post_enabled!==false)
        throw Error('Resultado não autorizado para conferência.');
      const parsed=normalizeDanfeDraft(result);
      setDraft({...parsed,items:parsed.items.map(item=>({
        ...item,...resolveDanfeCatalogItem(item,products)
      }))});
      setOrigin(result.source||'imagem');
    }catch(e){setError(e.message||'Não foi possível analisar o DANFE.')}
    finally{setBusy(false)}
  }
  function editItem(id,field,value){
    setFieldEdits(previous=>({...previous,[id+':'+field]:value}));
    updateItem(id,field,value);
  }
  const editValue=(item,field)=>fieldEdits[item.id+':'+field]??(item[field]??'');
  function updateItem(id,field,value){
    setDraft(previous=>previous&&({...previous,items:previous.items.map(item=>{
      if(item.id!==id)return item;
      const next={...item,[field]:value,confirmed:false};
      if(['unit','barcode','quantity'].includes(field)){
        if(field==='quantity')next.quantity=parseDanfeDecimal(value);
        if(field==='barcode')next.barcode=String(value||'').replace(/\D/g,'').slice(0,14);
        if(field==='unit')next.unit=exact(value).slice(0,12);
        const mapped=resolveDanfeCatalogItem(next,products);
        return {...next,...mapped};
      }
      if(field==='unitPrice'||field==='total')next[field]=parseDanfeDecimal(value);
      return next;
    })}));
  }
  function assignProduct(id){
    const text=exact(manualLookup[id]);
    const candidates=text?products.filter(product=>exact(product.sku)===text||exact(product.barcode)===text):[];
    setDraft(previous=>previous&&({...previous,items:previous.items.map(item=>{
      if(item.id!==id)return item;
      const product=candidates.length===1?candidates[0]:null;
      if(!product)return{...item,productId:'',matchStatus:candidates.length?'ambiguous':'unmatched',confirmed:false};
      if(product.active===false||!product.track_inventory)return{...item,productId:'',matchStatus:'inactive',confirmed:false};
      if(exact(product.unit)!==item.unit)return{...item,productId:'',matchStatus:'unit_mismatch',confirmed:false};
      if(!product.allow_fractional&&item.quantity!==null&&!Number.isInteger(item.quantity))
        return{...item,productId:'',matchStatus:'fractional',confirmed:false};
      return {...item,productId:product.id,matchStatus:'candidate',confirmed:false};
    })}));
  }
  const toggleConfirmed=(id,checked)=>setDraft(previous=>previous&&({
    ...previous,items:previous.items.map(item=>item.id!==id?item:{
      ...item,confirmed:Boolean(checked&&item.productId&&item.name&&item.quantity>0&&item.unitPrice!==null)
    })
  }));
  function exportCsv(){
    if(!draft?.items?.length)return;
    const csv=serializeDanfeDraftCSV(draft);
    const file=new Blob([csv],{type:'text/csv;charset=utf-8'});
    const url=URL.createObjectURL(file);
    const link=document.createElement('a');
    link.href=url;link.download='danfe-conferencia-nao-fiscal.csv';
    document.body.appendChild(link);link.click();link.remove();URL.revokeObjectURL(url);
  }
  return <section className="danfeReview" aria-label="Conferir mercadorias pelo DANFE sem certificado">
    <h3>Ler produtos do DANFE por foto ou PDF</h3>
    <p>Envie foto das páginas da nota ou um PDF. A IA do KAIZEN tentará reconhecer os
      produtos, quantidades e valores. <strong>É uma prévia, não uma consulta fiscal nem lançamento de estoque.</strong></p>
    <p><strong>Empresa em uso:</strong> {organization?.name||'—'}</p>
    <div className="danfeFiles">
      <label><Camera size={18}/> Fotografar páginas
        <input type="file" accept="image/jpeg,image/png,image/webp" capture="environment"
          onChange={e=>{setSelected(e.target.files);e.target.value=''}} disabled={busy}/>
      </label>
      <label><FileText size={18}/> Escolher PDF ou fotos (até 3 fotos)
        <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" multiple
          onChange={e=>{setSelected(e.target.files);e.target.value=''}} disabled={busy}/>
      </label>
    </div>
    {files.length>0&&<p className="danfeSelected" role="status">
      {files.length} arquivo(s) selecionado(s): {files.map(f=>f.name).join(', ')}
    </p>}
    <label className="danfeConsent">
      <input type="checkbox" checked={consent} onChange={e=>setConsent(e.target.checked)} disabled={busy}/>
      Autorizo analisar estas imagens ou PDF com a IA do KAIZEN. O documento será
      enviado ao provedor de IA habilitado e não será salvo no catálogo.
    </label>
    <button type="button" className="danfeAnalyze" disabled={!files.length||!consent||busy||!catalogReady||!canInventory}
      onClick={analyze}>
      <FileSearch size={19}/> {busy?'Analisando documento…':'Extrair itens para conferência'}
    </button>
    {!catalogReady&&<p role="status">Aguarde carregar todos os produtos do catálogo desta empresa.</p>}
    {!canInventory&&<p role="status">Seu usuário precisa de permissão de estoque para analisar DANFE.</p>}
    {error&&<p role="alert" className="danfeIssue">{error}</p>}
    {draft&&<div className="danfeDraft">
      <h4>Prévia extraída — {draft.items.length} item(ns) identificados</h4>
      <p>Origem: {origin==='pdf'?'PDF':'foto'} · Confiança informada pela IA: {draft.confidence}%.
        Confira cada campo com o documento original.</p>
      <div className="danfeMeta">
        <label>Chave da NF-e
          <input value={draft.accessKey} inputMode="numeric" maxLength={44}
            onChange={e=>setDraft(d=>({...d,accessKey:e.target.value.replace(/\D/g,'').slice(0,44)}))}/>
        </label>
        <label>CNPJ do fornecedor
          <input value={draft.supplierCnpj} inputMode="numeric" maxLength={14}
            onChange={e=>setDraft(d=>({...d,supplierCnpj:e.target.value.replace(/\D/g,'').slice(0,14)}))}/>
        </label>
        <label>CNPJ destinatário
          <input value={draft.recipientCnpj} inputMode="numeric" maxLength={14}
            onChange={e=>setDraft(d=>({...d,recipientCnpj:e.target.value.replace(/\D/g,'').slice(0,14)}))}/>
        </label>
      </div>
      {draft.warnings.map((warning,i)=><p className="danfeIssue" key={i}>{warning}</p>)}
      <div className="danfeReviewTable" role="region" aria-label="Itens extraídos com revisão">
        <table>
          <thead><tr><th>Descrição / fornecedor</th><th>GTIN</th><th>Unid.</th><th>Qtde.</th>
            <th>Preço un.</th><th>Produto do catálogo</th><th>Conferido</th></tr></thead>
          <tbody>{draft.items.map(item=><tr key={item.id}>
            <td><input aria-label={'Descrição item '+item.id} value={item.name}
              onChange={e=>updateItem(item.id,'name',e.target.value)}/>
              <small>#{item.id} · Fornecedor: {item.supplierCode||'sem código'}</small></td>
            <td><input aria-label={'GTIN item '+item.id} inputMode="numeric"
              value={item.barcode} onChange={e=>updateItem(item.id,'barcode',e.target.value)}/></td>
            <td><input aria-label={'Unidade item '+item.id} value={item.unit}
              onChange={e=>updateItem(item.id,'unit',e.target.value)}/></td>
            <td><input aria-label={'Quantidade item '+item.id} inputMode="decimal"
              value={editValue(item,'quantity')} onChange={e=>editItem(item.id,'quantity',e.target.value)}/></td>
            <td><input aria-label={'Preço unitário item '+item.id} inputMode="decimal"
              value={editValue(item,'unitPrice')} onChange={e=>editItem(item.id,'unitPrice',e.target.value)}/></td>
            <td><small>{statusTexts[item.matchStatus]||'Revisar'}:
              {products.find(p=>p.id===item.productId)?.name||'Nenhum vínculo'}</small>
              <div className="danfeMatch">
                <input aria-label={'SKU ou GTIN do catálogo para item '+item.id}
                  placeholder="SKU/GTIN exato" value={manualLookup[item.id]||''}
                  onChange={e=>setManualLookup(p=>({...p,[item.id]:e.target.value}))}/>
                <button type="button" onClick={()=>assignProduct(item.id)}>Vincular</button>
              </div>
            </td>
            <td><label className="danfeItemConfirm">
              <input type="checkbox" checked={item.confirmed}
                disabled={!item.productId||!item.name||!(item.quantity>0)||item.unitPrice===null}
                onChange={e=>toggleConfirmed(item.id,e.target.checked)}/> Conferido</label></td>
          </tr>)}</tbody>
        </table>
      </div>
      <div className="danfeSafety">
        <ShieldCheck size={20}/>
        <div><strong>Sem lançamento automático</strong>
        <p>Itens extraídos por IA exigem conferência. O DANFE não substitui o XML autorizado da NF-e.</p></div>
      </div>
      {issues.length>0&&<div className="danfeIssues"><strong>Pendências da conferência:</strong>
        <ul>{issues.map((msg,i)=><li key={i}>{msg}</li>)}</ul>
      </div>}
      <button type="button" className="danfeCsv" onClick={exportCsv} disabled={!draft.items.length}>
        <FileDown size={18}/> Exportar conferência CSV (não fiscal)
      </button>
    </div>}
  </section>;
}
